
import Themeable from "vuetify/lib/mixins/themeable";
import { Vue, Component } from "vue-property-decorator";
import { DataTableHeader } from "vuetify";
import DeleteVhostDialog from "@/components/dialogs/vhost/cdn/DeleteVhostDialog.vue";
import EditVhostDialog from "@/components/dialogs/vhost/cdn/EditVhostDialog.vue";
import CDNVhostRepositoryInterface, {
  CDNVhostInterface,
  CDNVhostStatusStrings,
} from "@/services/Vhost/CDN/Interfaces";
import SyncXDNsDialog from "@/components/dialogs/vhost/cdn/SyncXDNsDialog.vue";
import XDNFeaturesDialog from "@/components/dialogs/vhost/cdn/XDNFeaturesDialog.vue";
import ServersDialog from "@/components/dialogs/vhost/cdn/ServersDialog.vue";

@Component({
  mixins: [Themeable],
  inject: ["cdnVhostRepository"],
  components: {
    DeleteVhostDialog,
    EditVhostDialog,
    SyncXDNsDialog,
    XDNFeaturesDialog,
    ServersDialog,
  },
})
export default class AllVHosts extends Vue {
  public editVhostDialogData: CDNVhostInterface | false = false;

  public vhosts: CDNVhostInterface[] = [];

  public loading: boolean = true;

  public headers: DataTableHeader[] = [
    { text: "Website ID", value: "websiteId" },
    { text: "Domain", value: "domain" },
    { text: "Server", value: "originServerHostname" },
    { text: "Server IPs", value: "ips" },
    { text: "Status", value: "status" },
    { text: "XDN Enabled", value: "xdnEnabled" },
    { text: "Actions", value: "actions", sortable: false },
  ];

  public search: string = "";

  public cdnVhostRepository!: CDNVhostRepositoryInterface;

  public totalVhostsCount: number = 0;

  public options: { page: number; itemsPerPage: number } = {
    page: 1,
    itemsPerPage: 10,
  };

  public vhostStatusString: { [key: number]: string } = CDNVhostStatusStrings;

  public deleteVhostDialogData: CDNVhostInterface | false = false;
  public deleteVhostDialog: string = "";
  public editVhostDialog: string = "";

  public syncXDNsDialog: string = "";

  public xdnFeaturesDialog: string = "";
  public vhostForXDNFeaturesDialog: CDNVhostInterface = {} as CDNVhostInterface;

  public updateServerIpsDialog: string | false = false;

  public async handler(): Promise<void> {
    await this.updateTable();
  }

  public async mounted(): Promise<void> {
    await this.initialize();
    this.loading = false;
  }

  public async initialize(): Promise<void> {
    try {
      this.totalVhostsCount = await this.cdnVhostRepository.getVhostsCount();
      this.vhosts = await this.cdnVhostRepository.getVhostsBatch(
        this.options.page,
        this.options.itemsPerPage
      );
      this.$forceUpdate();
    } catch (e) {
      this.$errorDialog.show("Failed to retrieve Vhosts: " + e.message);
    }
  }

  public async getVhosts(): Promise<CDNVhostInterface[]> {
    try {
      return await this.cdnVhostRepository.getVhosts();
    } catch (e) {
      this.$errorDialog.show("Failed to retrieve Vhosts: " + e.message);
      return [];
    }
  }

  public createVhost(): void {
    this.$router.push({ name: "cdn_vhost_create" });
  }

  public async editVhostPrompt(vhost: CDNVhostInterface): Promise<void> {
    this.editVhostDialog = "edit-vhost-dialog";
    this.editVhostDialogData = vhost;
  }

  public async editVhost(vhost: CDNVhostInterface): Promise<void> {
    vhost.xdnEnabled
      ? await this.cdnVhostRepository.enableXDN(vhost.websiteId)
      : await this.cdnVhostRepository.disableXDN(vhost.websiteId);
  }

  public async clearCache(vhost: CDNVhostInterface): Promise<void> {
    try {
      await this.cdnVhostRepository.clearCache(vhost.websiteId);
      this.$successDialog.show(`Vhost ${vhost.domain} cache has been cleared.`);
    } catch (err) {
      this.$errorDialog.show(err.response.data.message);
    }
  }

  public async deleteVhostPrompt(server: CDNVhostInterface): Promise<void> {
    this.deleteVhostDialog = "delete-vhost-dialog";
    this.deleteVhostDialogData = server;
  }

  public async deleteVhost(vhost: CDNVhostInterface): Promise<void> {
    try {
      await this.cdnVhostRepository.deleteVhost(vhost.websiteId);
      this.$successDialog.show(`Vhost ${vhost.domain} has been removed.`);
      this.totalVhostsCount = await this.cdnVhostRepository.getVhostsCount();
      await this.updateTable();
    } catch (err) {
      this.$errorDialog.show(err.response.data.message);
    }
  }

  public async updateTable(): Promise<void> {
    if (this.search && this.search.trim() !== "") {
      return this.searchForVhost(false);
    }

    if (this.vhosts.length !== 0) {
      this.vhosts = await this.cdnVhostRepository.getVhostsBatch(
        this.options.page,
        this.options.itemsPerPage
      );
    }
  }

  public async searchForVhost(resetPage = true): Promise<void> {
    if (this.search.length > 0 && this.search.length < 3) {
      this.$errorDialog.show("Please be more specific about your search!");
      return;
    }

    if (resetPage) {
      this.options.page = 1;
    }

    this.vhosts = await this.cdnVhostRepository.getVhostsBatch(
      this.options.page,
      this.options.itemsPerPage,
      this.search
    );
  }

  public async updateCDN(
    vhost: CDNVhostInterface & { cdnEnabled: boolean }
  ): Promise<void> {
    try {
      if (vhost.cdnEnabled) {
        await this.cdnVhostRepository.disableCDN(vhost.websiteId);
      } else {
        await this.cdnVhostRepository.enableCDN(vhost.websiteId);
      }

      const index = this.vhosts.findIndex((v) => v.id === vhost.id);
      if (index === -1) {
        return;
      }

      this.vhosts[index].cdnEnabled = !vhost.cdnEnabled;
    } catch (err) {
      this.$errorDialog.show(err?.response?.data?.error || err.message);
    }
  }

  public showSyncXDNsDialog(): void {
    this.syncXDNsDialog = "SyncXDNsDialog";
  }

  public async syncXDNs(): Promise<void> {
    await this.cdnVhostRepository.syncXDNs();
  }

  public showXDNFeaturesDialog(vhost: CDNVhostInterface): void {
    this.vhostForXDNFeaturesDialog = vhost;
    this.xdnFeaturesDialog = "XDNFeaturesDialog";
  }

  public showServersDialog(): void {
    this.updateServerIpsDialog = "servers-dialog";
  }

  public async updateServerIps(serverId: string): Promise<void> {
    try {
      await this.cdnVhostRepository.updateServerIps(serverId);
      this.$successDialog.show('Server IPS Updated successfully.');

      await this.updateTable();
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message || err.message);
    }
  }
}
